import { supabase } from '@/integrations/supabase/client';
import { CaseStudyVersion, CaseStudySnapshot, CaseStudyVersionType } from '@/types/case-study';
import { createSnapshot } from '@/hooks/domains/case-study/useCaseStudyHistory';
import type { CaseStudyItem } from '@/types/case-study';

export interface CreateVersionData {
  case_study_id: string;
  label?: string;
  snapshot: CaseStudySnapshot;
  version_type?: CaseStudyVersionType;
}

/**
 * Service for managing case study versions
 * Handles creating, retrieving, and restoring versioned snapshots
 */
export class CaseStudyVersionService {

  /**
   * Create a new version of a case study
   */
  static async createVersion(
    caseStudy: CaseStudyItem,
    label?: string,
    versionType: CaseStudyVersionType = 'manual'
  ): Promise<CaseStudyVersion> {
    // Check authentication
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      throw new Error('User not authenticated');
    }

    // Create snapshot of current state
    const snapshot = createSnapshot(caseStudy);

    // Get next version number using RPC function
    const { data: versionNumber, error: versionError } = await supabase
      .rpc('get_next_version_number', { p_case_study_id: caseStudy.id });

    if (versionError) {
      console.error('[CaseStudyVersionService] Error getting version number:', versionError);
      throw versionError;
    }

    // Insert version record
    const { data: version, error } = await supabase
      .from('case_study_versions')
      .insert({
        case_study_id: caseStudy.id,
        version_number: versionNumber,
        label: label || null,
        snapshot: snapshot as any, // JSONB type
        created_by: user.id,
        version_type: versionType,
      })
      .select()
      .single();

    if (error) {
      console.error('[CaseStudyVersionService] Error creating version:', error);
      throw error;
    }

    return this.mapToVersionItem(version);
  }

  /**
   * Get all versions for a case study (ordered by version number descending)
   */
  static async getVersions(caseStudyId: string): Promise<CaseStudyVersion[]> {
    const { data: versions, error } = await supabase
      .from('case_study_versions')
      .select('*')
      .eq('case_study_id', caseStudyId)
      .order('version_number', { ascending: false });

    if (error) {
      console.error('[CaseStudyVersionService] Error fetching versions:', error);
      throw error;
    }

    return (versions || []).map(this.mapToVersionItem);
  }

  /**
   * Get a specific version by ID
   */
  static async getVersion(versionId: string): Promise<CaseStudyVersion> {
    const { data: version, error } = await supabase
      .from('case_study_versions')
      .select('*')
      .eq('id', versionId)
      .maybeSingle();

    if (error) {
      console.error('[CaseStudyVersionService] Error fetching version:', error);
      throw error;
    }

    if (!version) {
      throw new Error('Version not found');
    }

    return this.mapToVersionItem(version);
  }

  /**
   * Delete a version
   */
  static async deleteVersion(versionId: string): Promise<void> {
    const { error } = await supabase
      .from('case_study_versions')
      .delete()
      .eq('id', versionId);

    if (error) {
      console.error('[CaseStudyVersionService] Error deleting version:', error);
      throw error;
    }
  }

  /**
   * Get the latest version for a case study
   */
  static async getLatestVersion(caseStudyId: string): Promise<CaseStudyVersion | null> {
    const { data: version, error } = await supabase
      .from('case_study_versions')
      .select('*')
      .eq('case_study_id', caseStudyId)
      .order('version_number', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error('[CaseStudyVersionService] Error fetching latest version:', error);
      throw error;
    }

    return version ? this.mapToVersionItem(version) : null;
  }

  /**
   * Get count of versions for a case study
   */
  static async getVersionCount(caseStudyId: string): Promise<number> {
    const { count, error } = await supabase
      .from('case_study_versions')
      .select('*', { count: 'exact', head: true })
      .eq('case_study_id', caseStudyId);

    if (error) {
      console.error('[CaseStudyVersionService] Error getting version count:', error);
      throw error;
    }

    return count || 0;
  }

  /**
   * Check if a version exists for a case study with specific criteria
   */
  static async hasVersionWithType(
    caseStudyId: string,
    versionType: CaseStudyVersionType
  ): Promise<boolean> {
    const { data, error } = await supabase
      .from('case_study_versions')
      .select('id')
      .eq('case_study_id', caseStudyId)
      .eq('version_type', versionType)
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error('[CaseStudyVersionService] Error checking version type:', error);
      return false;
    }

    return !!data;
  }

  /**
   * Map database row to CaseStudyVersion type
   */
  private static mapToVersionItem(data: Record<string, any>): CaseStudyVersion {
    return {
      id: data.id,
      case_study_id: data.case_study_id,
      version_number: data.version_number,
      label: data.label || undefined,
      snapshot: data.snapshot as CaseStudySnapshot,
      created_by: data.created_by,
      created_at: data.created_at,
      version_type: data.version_type,
    };
  }
}
